<script setup lang="ts">
const { t } = useI18n();
const localePath = useLocalePath();
const slug = useRoute().params.slug as string;
const { data: contract } = await useAsyncData(slug, () => queryCollection("contracts").where("slug", "=", slug).first());

if (!contract.value) {
  throw createError({
    statusCode: 404,
    message: "Page introuvable",
  });
}

const features = computed(() => [
  {
    name: t('contractPage.features.timeSaving.name'),
    description: t('contractPage.features.timeSaving.description'),
    icon: "lucide:timer",
  },
  {
    name: t('contractPage.features.fairPrice.name'),
    description: t('contractPage.features.fairPrice.description'),
    icon: "lucide:piggy-bank",
  },
  {
    name: t('contractPage.features.infiniteKnowledge.name'),
    description: t('contractPage.features.infiniteKnowledge.description'),
    icon: "lucide:brain",
  },
]);

const pictureUrl = computed(() => contract.value?.screenshot || "/assets/screenshot-1.png");

const { openVideoModal } = useVideoModal();

const createContractLink = computed(() => {
  if (!contract.value?.prompt) return localePath("/app");
  return `${localePath('/app/new')}?prompt=${encodeURIComponent(contract.value.prompt)}`;
});

if (contract.value.seo)
  useSeoMeta({
    title: contract.value.seo.title,
    description: contract.value.seo.description,
  });

useSchemaOrg([
  defineArticle(),
]);
</script>

<template>
  <main class="relative">
    <div
      class="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
      aria-hidden="true"
    >
      <div
        class="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-blue-300 to-primary-300 opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
        style="clip-path: polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)"
      />
    </div>

    <div class="relative isolate -mb-24 overflow-hidden">
      <!-- <svg
        class="absolute inset-0 -z-10 size-full stroke-gray-200 [mask-image:radial-gradient(100%_100%_at_top_right,white,transparent)]"
        aria-hidden="true"
      >
        <defs>
          <pattern
            id="0787a7c5-978c-4f66-83c7-11c213f99cb7"
            width="200"
            height="200"
            x="50%"
            y="-1"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M.5 200V.5H200"
              fill="none"
            />
          </pattern>
        </defs>
        <rect
          width="100%"
          height="100%"
          stroke-width="0"
          fill="url(#0787a7c5-978c-4f66-83c7-11c213f99cb7)"
        />
      </svg> -->
      <div class="mx-auto max-w-7xl px-6 pt-10 lg:flex lg:px-8 lg:pt-40">
        <div class="mx-auto max-w-2xl lg:mx-0 lg:shrink-0 lg:pt-8">
          <!-- <div class="mt-24 sm:mt-32 lg:mt-16">
            <a
              href="#"
              class="inline-flex space-x-6"
            >
              <span class="rounded-full bg-primary-600/10 px-3 py-1 text-sm/6 font-semibold text-primary-600 ring-1 ring-inset ring-primary-600/10">What's new</span>
              <span class="inline-flex items-center space-x-2 text-sm/6 font-medium text-gray-600">
                <span>Just shipped v1.0</span>
                <ChevronRightIcon
                  class="size-5 text-gray-400"
                  aria-hidden="true"
                />
              </span>
            </a>
          </div> -->
          <h1 class="mt-20 font-serif text-4xl font-semibold text-gray-900 sm:mt-12 sm:text-5xl">
            {{ contract?.leading }} <span class="capitalize text-primary-600">{{ contract?.title }}</span> {{ contract?.trailing }}
          </h1>
          <p class="mt-8 text-pretty text-lg font-medium text-gray-500 sm:text-xl/8">
            {{ t('contractPage.describeAndGet') }}
          </p>
          <div class="mt-10 flex items-center gap-x-6">
            <UButton
              leading-icon="lucide:square-pen"
              size="lg"
              :to="createContractLink"
            >
              {{ t('contractPage.createMyContract') }}
            </UButton>
            <UButton
              variant="soft"
              size="lg"
              :to="localePath('/')"
            >
              {{ t('contractPage.learnMore') }}
            </UButton>
          </div>
          <div class="mt-20 flex items-center gap-x-4">
            <h4 class="flex-none text-sm/6 font-semibold text-primary-600">
              {{ t('contractPage.useContratIA') }}
            </h4>
            <div class="h-px flex-auto bg-gray-100" />
          </div>
          <ul
            role="list"
            class="mt-8 grid grid-cols-1 gap-4 text-sm/6 text-gray-600 sm:grid-cols-2 sm:gap-6"
          >
            <li
              v-for="reason in contract?.reasons"
              :key="reason"
              class="flex gap-x-3"
            >
              <Icon
                name="heroicons:check-16-solid"
                class="h-6 w-5 flex-none text-primary-600"
                aria-hidden="true"
              />
              {{ reason }}
            </li>
          </ul>
        </div>
        <div class="mx-auto mb-24 mt-16 flex w-full sm:mt-24 lg:ml-10 lg:mr-0 lg:mt-0 lg:max-w-none lg:flex-none xl:ml-32">
          <div class="w-full flex-none sm:max-w-3xl">
            <div class="-m-2 rounded-xl bg-gray-900/5 p-2 ring-1 ring-inset ring-gray-900/10 lg:-m-4 lg:rounded-2xl lg:p-4">
              <NuxtImg
                :src="pictureUrl"
                :alt="`${contract?.title} Modèle sur Contrat IA`"
                class="w-[76rem] rounded-md shadow-2xl ring-1 ring-gray-900/10"
                sizes="sm:768px md:768px lg:1216px"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="absolute inset-x-0 top-[calc(50vh-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(50vh-30rem)]"
      aria-hidden="true"
    >
      <div
        class="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%+36rem)] sm:w-[72.1875rem]"
        style="clip-path: polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)"
      />
    </div>

    <Faq
      v-if="contract?.faq"
      :faqs="contract.faq"
    />

    <div class="mx-auto my-24 max-w-7xl px-6 sm:my-32 lg:px-8 xl:my-48">
      <div class="mx-auto max-w-2xl lg:text-center">
        <h2 class="text-center text-base/7 font-semibold text-primary-600">
          {{ t('contractPage.whyContratIA') }}
        </h2>
        <p class="mx-auto mt-2 max-w-xl text-balance text-center font-serif text-4xl font-semibold text-gray-900 sm:text-5xl">
          <span class="hidden sm:inline">
            {{ t('contractPage.whyEntrepreneursChoose') }}
          </span>
          <span class="sm:hidden">
            {{ t('contractPage.whyEntrepreneursChooseMobile') }}
          </span>
        </p>
        <p class="mt-6 text-lg/8 text-gray-600">
          {{ t('contractPage.discoverReasons') }}
        </p>
      </div>
      <div class="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
        <dl class="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
          <div
            v-for="feature in features"
            :key="feature.name"
            class="flex flex-col"
          >
            <dt class="flex items-center gap-x-3 text-base/7 font-semibold text-gray-900">
              <Icon
                :name="feature.icon"
                class="size-5 flex-none text-indigo-600"
                aria-hidden="true"
              />
              {{ feature.name }}
            </dt>
            <dd class="mt-4 flex flex-auto flex-col text-base/7 text-gray-600">
              <p class="flex-auto">
                {{ feature.description }}
              </p>
            </dd>
          </div>
        </dl>
      </div>

      <div class="mt-20 flex items-center justify-center gap-x-6">
        <NuxtLink
          class="inline-flex shrink-0 items-center gap-x-2.5 rounded-lg border-0 bg-primary-600 px-3.5 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-primary-500 focus:outline-none focus-visible:outline-0 focus-visible:ring-2 focus-visible:ring-primary-600 disabled:cursor-not-allowed disabled:bg-primary-400 disabled:opacity-75 aria-disabled:cursor-not-allowed aria-disabled:bg-primary-400 aria-disabled:opacity-75 dark:bg-primary-800 dark:hover:bg-primary-700/50 dark:focus-visible:ring-primary-400 dark:disabled:bg-primary-800 dark:aria-disabled:bg-primary-800"
          :to="createContractLink"
        >
          <Icon
            name="lucide:arrow-right"
            class="size-5"
            aria-hidden="true"
          />
          {{ t('contractPage.createContract') }}
        </NuxtLink>
        <NuxtLinkLocale
          class="inline-flex shrink-0 items-center gap-x-2.5 rounded-lg bg-primary-50 px-3.5 py-2.5 text-sm font-medium text-primary-500 hover:bg-primary-100 focus:outline-none focus-visible:outline-0 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:bg-primary-50 disabled:opacity-75 aria-disabled:cursor-not-allowed aria-disabled:bg-primary-50 aria-disabled:opacity-75 dark:bg-primary-950 dark:text-primary-400 dark:hover:bg-primary-900 dark:focus-visible:ring-primary-400 dark:disabled:bg-primary-950 dark:aria-disabled:bg-primary-950"
          to="/"
        >
          {{ t('contractPage.learnMore') }}
        </NuxtLinkLocale>
      </div>
    </div>

    <div class="my-24 sm:my-32 xl:my-48">
      <div class="mx-auto max-w-2xl px-6 lg:max-w-7xl lg:px-8">
        <h2 class="text-center text-base/7 font-semibold text-primary-600">
          {{ t('contractPage.unMatchedSimplicity') }}
        </h2>
        <p class="mx-auto mt-2 max-w-lg text-balance text-center font-serif text-4xl font-semibold text-gray-900 sm:text-5xl">
          {{ t('contractPage.simplestWay') }}
        </p>
      </div>
      <div
        role="button"
        tabindex="0"
        :aria-label="t('contractPage.watchDemoAriaLabel')"
        class="group mx-auto mt-10 w-full max-w-3xl flex-none cursor-pointer p-4 sm:max-w-5xl"
        @click="openVideoModal"
        @keydown.enter="openVideoModal"
        @keydown.space.prevent="openVideoModal"
      >
        <div class="relative -m-2 rounded-xl bg-gray-900/5 p-2 ring-1 ring-inset ring-gray-900/10 lg:-m-4 lg:rounded-2xl lg:p-4">
          <NuxtImg
            :src="pictureUrl"
            loading="lazy"
            :alt="`${contract?.title} Template sur Contrat IA`"
            class="w-[76rem] rounded-md shadow-2xl ring-1 ring-gray-900/10"
            sizes="sm:768px md:768px lg:1216px"
          />
          <AppPlayButtonOverlay />
        </div>
      </div>
    </div>

    <LandingEndCta />
  </main>
</template>
